// Capa FINA sobre atlasBackendGet/Fetch para `/v1/analisis-imagenes/*` (contrato §6 de la
// spec 2026-09-03-analisis-fidelidad-test-masivo-design.md). Acá no hay lógica: se
// interpreta el sobre `{ response: 'success' | 'error' }` y se tira Error con el
// `message` del backend para que cada vista lo muestre en pantalla.

import { atlasBackendFetch, atlasBackendGet } from '../../../lib/atlasBackend';
import type {
  Accion,
  BodyCrearRun,
  Caso,
  ClasificacionCreada,
  ClasificacionMotivos,
  ComparacionCreada,
  ComparacionIA,
  ConfigAnalisis,
  Dataset,
  Referencia,
  EstadoVeredicto,
  MotivoConImagen,
  ParamsDataset,
  RunCreada,
  RunDetalle,
  RunResumen,
} from './tipos';

const BASE = '/v1/analisis-imagenes';

interface SobreError {
  response: 'error';
  error_type?: string;
  message?: string;
}

/**
 * Lee el JSON y valida el sobre. 202 cuenta como éxito (POST /runs encola y responde
 * antes de terminar). 403 `auth` = la cuenta no es dev_tester: se dice explícito porque
 * el gate de la página ya debería haberlo filtrado y si pasa es un bug de config.
 */
async function leer<T>(res: Response, que: string): Promise<T> {
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  const sobre = (json ?? {}) as Partial<SobreError> & Record<string, unknown>;
  if (res.status === 403) {
    throw new Error(sobre.message ?? `${que}: la cuenta no está habilitada (403 auth)`);
  }
  if (sobre.response === 'error' || !(res.ok || res.status === 202)) {
    const msg = sobre.message ?? `${que}: HTTP ${res.status}`;
    const tipo = sobre.error_type ? ` [${sobre.error_type}]` : '';
    throw new Error(`${msg}${tipo}`);
  }
  if (sobre.response !== 'success') {
    throw new Error(`${que}: respuesta inesperada del backend (sin response=success)`);
  }
  return sobre as unknown as T;
}

export async function fetchConfig(): Promise<ConfigAnalisis> {
  const res = await atlasBackendGet(`${BASE}/config`);
  return leer<ConfigAnalisis>(res, 'config');
}

export async function fetchDataset(params: ParamsDataset): Promise<Dataset> {
  const qs = new URLSearchParams({
    rubro: params.rubro,
    desde: params.desde,
    prioridad_max: String(params.prioridad_max),
    por_empresa: String(params.por_empresa),
    limit: String(params.limit),
    control: String(params.control),
  });
  const res = await atlasBackendGet(`${BASE}/dataset?${qs.toString()}`);
  return leer<Dataset>(res, 'dataset');
}

/**
 * Casos por id (mismo shape que el dataset, URLs firmadas frescas). Es el camino de vuelta
 * de la persistencia: se guardan ids, no tarjetas. Los `control_ids` vuelven con
 * `tipo_caso: 'control'`.
 */
export async function fetchCasosPorIds(imagenIds: string[], controlIds: string[]): Promise<Caso[]> {
  const res = await atlasBackendFetch(`${BASE}/casos`, { imagen_ids: imagenIds, control_ids: controlIds });
  const r = await leer<{ casos: Caso[] }>(res, 'casos por id');
  return r.casos ?? [];
}

/** 202 → `{ run_id, execution_id, total }`. El body ya viene sin `prompt_override` si es el default. */
export async function crearRun(body: BodyCrearRun): Promise<RunCreada> {
  const res = await atlasBackendFetch(`${BASE}/runs`, body as unknown as Record<string, unknown>);
  const r = await leer<RunCreada>(res, 'crear corrida');
  return { run_id: r.run_id, execution_id: r.execution_id, total: r.total };
}

export async function listarRuns(): Promise<RunResumen[]> {
  const res = await atlasBackendGet(`${BASE}/runs`);
  const r = await leer<{ runs: RunResumen[] }>(res, 'listar corridas');
  return r.runs ?? [];
}

export async function fetchRun(runId: string): Promise<RunDetalle> {
  const res = await atlasBackendGet(`${BASE}/runs/${encodeURIComponent(runId)}`);
  const r = await leer<RunDetalle>(res, 'corrida');
  return { run: r.run, resultados: r.resultados ?? [], casos: r.casos ?? [] };
}

/** `estado: null` borra el veredicto (click de nuevo sobre el activo). */
export async function guardarVeredicto(
  runId: string,
  imagenId: string,
  estado: EstadoVeredicto | null,
  nota?: string,
): Promise<void> {
  const body: Record<string, unknown> = { imagen_id: imagenId, estado };
  if (nota !== undefined) body.nota = nota;
  const res = await atlasBackendFetch(`${BASE}/runs/${encodeURIComponent(runId)}/veredicto`, body);
  await leer<Record<string, never>>(res, 'veredicto');
}

export async function cancelarRun(runId: string): Promise<void> {
  const res = await atlasBackendFetch(`${BASE}/runs/${encodeURIComponent(runId)}/cancelar`, {});
  await leer<Record<string, never>>(res, 'cancelar corrida');
}

/** Renombra la corrida; devuelve la etiqueta guardada (el backend trimmea). 400 vacía, 404 no existe. */
export async function renombrarRun(runId: string, etiqueta: string): Promise<string> {
  const res = await atlasBackendFetch(`${BASE}/runs/${encodeURIComponent(runId)}/renombrar`, { etiqueta });
  const r = await leer<{ etiqueta: string }>(res, 'renombrar corrida');
  return r.etiqueta ?? etiqueta;
}

/** El system prompt EXACTO que usó la corrida (default o override). */
export async function fetchPromptDeRun(runId: string): Promise<string> {
  const res = await atlasBackendGet(`${BASE}/runs/${encodeURIComponent(runId)}/prompt`);
  const r = await leer<{ prompt: string }>(res, 'prompt de la corrida');
  return r.prompt ?? '';
}

// ---- Referencias humanas y comparaciones con IA ----

export async function listarReferencias(imagenIds: string[]): Promise<Referencia[]> {
  if (imagenIds.length === 0) return [];
  const res = await atlasBackendFetch(`${BASE}/referencia/listar`, { imagen_ids: imagenIds });
  const r = await leer<{ referencias: Referencia[] }>(res, 'referencias');
  return r.referencias ?? [];
}

/** Guarda (o borra con `accion: null`) el análisis humano de UNA imagen. Devuelve lo guardado, o null si se borró. */
export async function guardarReferencia(imagenId: string, accion: Accion | null, texto?: string): Promise<Referencia | null> {
  const body: Record<string, unknown> = { imagen_id: imagenId, accion };
  if (texto !== undefined) body.texto = texto;
  const res = await atlasBackendFetch(`${BASE}/referencia`, body);
  const r = await leer<{ referencia: Referencia | null }>(res, 'referencia');
  return r.referencia ?? null;
}

/** 202 → `{ comparacion_id, execution_id, total_casos }`. 400 con mensaje si no hay referencias o casos en común. */
export async function crearComparacion(runIds: string[], modelo?: string, imagenIds?: string[]): Promise<ComparacionCreada> {
  const body: Record<string, unknown> = { run_ids: runIds };
  if (modelo) body.modelo = modelo;
  if (imagenIds && imagenIds.length > 0) body.imagen_ids = imagenIds;
  const res = await atlasBackendFetch(`${BASE}/comparaciones`, body);
  const r = await leer<ComparacionCreada>(res, 'comparar con IA');
  return { comparacion_id: r.comparacion_id, execution_id: r.execution_id, total_casos: r.total_casos };
}

/** Últimas 50, sin `resultado`. */
export async function listarComparaciones(): Promise<ComparacionIA[]> {
  const res = await atlasBackendGet(`${BASE}/comparaciones`);
  const r = await leer<{ comparaciones: ComparacionIA[] }>(res, 'listar comparaciones');
  return r.comparaciones ?? [];
}

export async function fetchComparacion(id: string): Promise<ComparacionIA> {
  const res = await atlasBackendGet(`${BASE}/comparaciones/${encodeURIComponent(id)}`);
  const r = await leer<{ comparacion: ComparacionIA }>(res, 'comparación');
  return r.comparacion;
}

// ---- Clasificación con IA del motivo de las ediciones ----

/**
 * Encola la clasificación de las imágenes que NO tienen motivo cacheado (`forzar` reclasifica
 * todas). 202 → `{ clasificacion_id, execution_id, pendientes, ya_clasificadas }`; si no había
 * nada pendiente el backend responde 200 con `clasificacion_id: null` y no hay que pollear.
 */
export async function crearClasificacion(imagenIds: string[], modelo?: string, forzar?: boolean): Promise<ClasificacionCreada> {
  const body: Record<string, unknown> = { imagen_ids: imagenIds };
  if (modelo) body.modelo = modelo;
  if (forzar) body.forzar = true;
  const res = await atlasBackendFetch(`${BASE}/clasificaciones`, body);
  const r = await leer<ClasificacionCreada>(res, 'clasificar ediciones');
  return {
    clasificacion_id: r.clasificacion_id ?? null,
    execution_id: r.execution_id ?? null,
    pendientes: r.pendientes ?? 0,
    ya_clasificadas: r.ya_clasificadas ?? 0,
  };
}

export async function fetchClasificacion(id: string): Promise<ClasificacionMotivos> {
  const res = await atlasBackendGet(`${BASE}/clasificaciones/${encodeURIComponent(id)}`);
  const r = await leer<{ clasificacion: ClasificacionMotivos }>(res, 'clasificación');
  return r.clasificacion;
}

/** Motivos cacheados de esas imágenes (solo las que tienen). Sin ids no llama: no hay nada que traer. */
export async function listarMotivos(imagenIds: string[]): Promise<MotivoConImagen[]> {
  if (imagenIds.length === 0) return [];
  const res = await atlasBackendFetch(`${BASE}/motivos/listar`, { imagen_ids: imagenIds });
  const r = await leer<{ motivos: MotivoConImagen[] }>(res, 'motivos de edición');
  return r.motivos ?? [];
}
